import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Input, Label } from "reactstrap";
import notifications from "../../common/Components/Notifications/Notification";
import { canUseGiRegistry } from "../../helpers/geoEnabled";
import {
  approveGiChange,
  createGi,
  freezeGi,
  getGi,
  previewGiFence,
  rejectGi,
  rejectGiChange,
  updateGi,
  uploadGiPdf,
} from "../../services/geoGiApi";
import { listLoomPins } from "../../services/geoLoomApi";
import GiFenceMap from "./GiFenceMap";
import { parseGeoPath, unwrapGeoPayload } from "./geoPath";
import { GI_STATUS, giStatusClass, giStatusLabel } from "./giStatus";
import "./gi-registry.scss";

type PlaceRow = { placeName: string; lat: string; lng: string };

const emptyPlace = (): PlaceRow => ({ placeName: "", lat: "", lng: "" });

const readAuthUser = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null") || {};
  } catch {
    return {};
  }
};

const toPlaceRows = (places: any[]): PlaceRow[] => {
  if (!Array.isArray(places) || places.length === 0) {
    return [emptyPlace()];
  }
  return places.map((p) => ({
    placeName: p?.placeName || "",
    lat: p?.lat === 0 || p?.lat ? String(p.lat) : "",
    lng: p?.lng === 0 || p?.lng ? String(p.lng) : "",
  }));
};

const validPlaces = (rows: PlaceRow[]) =>
  rows
    .map((row) => ({
      placeName: (row.placeName || "").trim(),
      lat: Number(row.lat),
      lng: Number(row.lng),
    }))
    .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));

const parseGeojsonObject = (text: string): Record<string, unknown> | null => {
  const trimmed = text.trim();
  if (!trimmed) {
    return null;
  }
  const parsed = JSON.parse(trimmed);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("GeoJSON must be an object");
  }
  return parsed as Record<string, unknown>;
};

const hullToText = (hull: any): string => {
  if (!hull) {
    return "";
  }
  try {
    return JSON.stringify(hull, null, 2);
  } catch {
    return "";
  }
};

const GiRegistryForm = () => {
  const history = useHistory();
  const location = useLocation();
  const path = parseGeoPath(location.pathname, "gi-registry");
  const giId = path.id;
  const isNew = path.isNew;
  const isEdit = path.isEdit;
  const readOnly = !isEdit;
  const authUser: any = readAuthUser();
  const role = authUser?.users?.role;
  const isSuperAdmin = role === "SUPER_ADMIN";

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [record, setRecord] = useState<any>(null);
  const [name, setName] = useState("");
  const [goodsDescription, setGoodsDescription] = useState("");
  const [state, setState] = useState("");
  const [notes, setNotes] = useState("");
  const [documentUrl, setDocumentUrl] = useState("");
  const [places, setPlaces] = useState<PlaceRow[]>([emptyPlace()]);
  const [geojsonText, setGeojsonText] = useState("");
  const [geojsonError, setGeojsonError] = useState("");
  const [rejectNote, setRejectNote] = useState("");
  const [draftHull, setDraftHull] = useState<any>(null);
  const [approximate, setApproximate] = useState(false);
  const [fenceSource, setFenceSource] = useState("");
  const [loomPins, setLoomPins] = useState<any[]>([]);
  const [applicationNumber, setApplicationNumber] = useState("");
  const [goodsClasses, setGoodsClasses] = useState("");
  const [category, setCategory] = useState("");
  const [geographicalAreaText, setGeographicalAreaText] = useState("");
  const [ipIndiaDetailUrl, setIpIndiaDetailUrl] = useState("");
  const [sourceDocument, setSourceDocument] = useState("");
  const [sourceJournalPage, setSourceJournalPage] = useState("");
  const [boundaryStatus, setBoundaryStatus] = useState("");
  const [centroidUseWarning, setCentroidUseWarning] = useState("");
  const [seedApproximate, setSeedApproximate] = useState(false);
  const [statusForSeed, setStatusForSeed] = useState("");
  const [sourceType, setSourceType] = useState("");
  const [lastVerifiedAt, setLastVerifiedAt] = useState("");
  const [boundarySourceType, setBoundarySourceType] = useState("");
  const [boundaryConfidence, setBoundaryConfidence] = useState("");
  const [centroidLat, setCentroidLat] = useState("");
  const [centroidLng, setCentroidLng] = useState("");
  const [geojsonAvailable, setGeojsonAvailable] = useState("");

  const applyView = (view: any) => {
    setRecord(view);
    const live = view || {};
    const pending = view?.pendingChange;
    setName(pending?.name || live.name || live.nameDisplay || "");
    setGoodsDescription(pending?.goodsDescription || live.goodsDescription || "");
    setState(pending?.state || live.state || "");
    setNotes(pending?.notes || live.notes || "");
    setDocumentUrl(pending?.documentUrl || live.documentUrl || "");
    setPlaces(toPlaceRows(pending?.places || live.places));
    const sourceKind = pending?.fenceSource || live.fenceSource || "";
    const hull = pending?.proposedHull || live.liveHull;
    if (hull) {
      setGeojsonText(hullToText(hull));
      setDraftHull(hull);
    } else {
      setGeojsonText("");
      setDraftHull(null);
    }
    setFenceSource(sourceKind);
    setRejectNote(live.rejectNote || "");
    setApplicationNumber(live.applicationNumber || "");
    setGoodsClasses(live.goodsClasses || "");
    setCategory(live.category || "");
    setGeographicalAreaText(live.geographicalAreaText || "");
    setIpIndiaDetailUrl(live.ipIndiaDetailUrl || "");
    setSourceDocument(live.sourceDocument || "");
    setSourceJournalPage(live.sourceJournalPage || "");
    setBoundaryStatus(live.boundaryStatus || "");
    setCentroidUseWarning(live.centroidUseWarning || "");
    setSeedApproximate(!!live.seedApproximate);
    setStatusForSeed(live.statusForSeed || "");
    setSourceType(live.sourceType || "");
    setLastVerifiedAt(live.lastVerifiedAt || "");
    setBoundarySourceType(live.boundarySourceType || "");
    setBoundaryConfidence(live.boundaryConfidence || "");
    setCentroidLat(live.centroidLat === 0 || live.centroidLat ? String(live.centroidLat) : "");
    setCentroidLng(live.centroidLng === 0 || live.centroidLng ? String(live.centroidLng) : "");
    setGeojsonAvailable(live.geojsonAvailable || "");
  };

  const loadGi = async (id: string) => {
    setLoading(true);
    try {
      const res: any = await getGi(id);
      const view = unwrapGeoPayload(res);
      if (!res?.success || !view) {
        notifications({
          title: "Error!",
          message: res?.message || "GI not found",
          type: "danger",
        });
        history.replace("/gi-registry");
        return;
      }
      applyView(view);
    } catch {
      notifications({
        title: "Error!",
        message: "Unable to load GI",
        type: "danger",
      });
      history.replace("/gi-registry");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canUseGiRegistry(role)) {
      history.replace("/dashboard");
      return;
    }
    if (!isNew && giId) {
      loadGi(giId);
    }
  }, [giId, isNew]);

  useEffect(() => {
    if (isNew || !giId) {
      setLoomPins([]);
      return;
    }
    listLoomPins(giId).then((res: any) => {
      if (res?.success && Array.isArray(res.data)) {
        setLoomPins(res.data);
      }
    });
  }, [giId, isNew]);

  useEffect(() => {
    if (readOnly) {
      return;
    }
    const payload = validPlaces(places);
    const timer = window.setTimeout(async () => {
      let geojson: Record<string, unknown> | null = null;
      if (geojsonText.trim()) {
        try {
          geojson = parseGeojsonObject(geojsonText);
          setGeojsonError("");
        } catch (err: any) {
          setGeojsonError(err?.message || "GeoJSON is not valid JSON");
          setDraftHull(null);
          return;
        }
      } else {
        setGeojsonError("");
      }
      if (!geojson && payload.length === 0) {
        setDraftHull(null);
        setApproximate(false);
        setFenceSource("");
        return;
      }
      try {
        const res: any = await previewGiFence({
          places: payload,
          geojson: geojson || undefined,
        });
        if (res?.success) {
          setDraftHull(res?.data?.hull || null);
          setApproximate(!!res?.data?.approximate);
          setFenceSource(res?.data?.fenceSource || "");
        } else {
          setDraftHull(null);
          if (geojson) {
            setGeojsonError(res?.message || "Could not read this GeoJSON");
          }
        }
      } catch (err: any) {
        setDraftHull(null);
        if (geojson) {
          setGeojsonError(err?.message || "Could not read this GeoJSON");
        }
      }
    }, 400);
    return () => window.clearTimeout(timer);
  }, [places, geojsonText, readOnly]);

  const status = record?.status;
  const hasOpenChange = !!record?.hasOpenChange || !!record?.pendingChange;
  const frozen = status === GI_STATUS.FROZEN;
  const pendingReview = status === GI_STATUS.PENDING_REVIEW || status === GI_STATUS.REJECTED;

  const liveHull = frozen ? record?.liveHull : draftHull || record?.liveHull;
  const proposedHull = frozen ? draftHull : undefined;
  const mapPlaces = useMemo(
    () =>
      validPlaces(places).map((p) => ({
        placeName: p.placeName,
        lat: p.lat,
        lng: p.lng,
      })),
    [places]
  );

  const buildBody = () => {
    const trimmedName = name.trim();
    const payloadPlaces = validPlaces(places);
    let geojson: Record<string, unknown> | null = null;
    if (geojsonText.trim()) {
      try {
        geojson = parseGeojsonObject(geojsonText);
      } catch (err: any) {
        notifications({
          title: "Error!",
          message: err?.message || "GeoJSON is not valid JSON",
          type: "danger",
        });
        return null;
      }
    }
    if (!trimmedName) {
      notifications({
        title: "Error!",
        message: "GI name is required",
        type: "danger",
      });
      return null;
    }
    if (payloadPlaces.length === 0 && !geojson) {
      if (!(isNew && applicationNumber.trim())) {
        notifications({
          title: "Error!",
          message: "Add district points or paste a GeoJSON polygon",
          type: "danger",
        });
        return null;
      }
    }
    return {
      name: trimmedName,
      goodsDescription: goodsDescription.trim() || undefined,
      state: state.trim() || undefined,
      notes: notes.trim() || undefined,
      documentUrl: documentUrl.trim() || undefined,
      places: payloadPlaces,
      geojson: geojson || undefined,
      applicationNumber: applicationNumber.trim() || "",
      goodsClasses: goodsClasses.trim() || "",
      category: category.trim() || "",
      geographicalAreaText: geographicalAreaText.trim() || "",
      ipIndiaDetailUrl: ipIndiaDetailUrl.trim() || "",
      sourceDocument: sourceDocument.trim() || "",
      sourceJournalPage: sourceJournalPage.trim() || "",
      sourceType: sourceType.trim() || "",
      lastVerifiedAt: lastVerifiedAt.trim() || "",
      statusForSeed: statusForSeed.trim() || "",
      boundaryStatus: boundaryStatus.trim() || "",
      boundarySourceType: boundarySourceType.trim() || "",
      boundaryConfidence: boundaryConfidence.trim() || "",
      centroidLat: centroidLat.trim() && Number.isFinite(Number(centroidLat)) ? Number(centroidLat) : undefined,
      centroidLng: centroidLng.trim() && Number.isFinite(Number(centroidLng)) ? Number(centroidLng) : undefined,
      centroidUseWarning: centroidUseWarning.trim() || "",
      geojsonAvailable: geojsonAvailable.trim() || "",
    };
  };

  const persist = async (): Promise<{ id: string; alreadyRegistered?: boolean } | null> => {
    const body = buildBody();
    if (!body) {
      return null;
    }
    const res: any = isNew ? await createGi(body) : await updateGi(giId as string, body);
    if (!res?.success) {
      notifications({
        title: "Error!",
        message: res?.message || "Could not save GI",
        type: "danger",
      });
      return null;
    }
    const saved = unwrapGeoPayload(res);
    if (saved) {
      applyView(saved);
    }
    const id = saved?.id || saved?._id || giId || null;
    if (!id) {
      return null;
    }
    return { id: String(id), alreadyRegistered: Boolean(saved?.alreadyRegistered) };
  };

  const withBusy = async (work: Promise<void>) => {
    setSaving(true);
    try {
      await work;
    } catch {
      notifications({
        title: "Error!",
        message: "Something went wrong",
        type: "danger",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSendForReview = () => {
    void withBusy((async () => {
      const saved = await persist();
      if (!saved) {
        return;
      }
      if (saved.alreadyRegistered) {
        notifications({
          title: "Added to studio",
          message: "This GI is already registered. It is now part of your studio. Authorised user details can be added later.",
          type: "success",
        });
        history.replace(`/gi-registry/${saved.id}/authorised-user`);
        return;
      }
      notifications({
        title: "Success!",
        message: frozen
          ? isSuperAdmin
            ? "Live GI updated"
            : "Changes sent for super admin review"
          : "Sent for review",
        type: "success",
      });
      if (isNew) {
        history.replace(`/gi-registry/${saved.id}/edit`);
      }
    })());
  };

  const handleApproveAndFreeze = () => {
    void withBusy((async () => {
      if (!window.confirm("Approve and freeze this GI? The live fence stays until a later fence change.")) {
        return;
      }
      const saved = await persist();
      if (!saved) {
        return;
      }
      const res: any = await freezeGi(saved.id);
      if (!res?.success) {
        notifications({
          title: "Error!",
          message: res?.message || "Could not freeze GI",
          type: "danger",
        });
        return;
      }
      applyView(unwrapGeoPayload(res));
      notifications({
        title: "Success!",
        message: res?.message || "GI frozen",
        type: "success",
      });
    })());
  };

  const handleAskForChanges = () => {
    void withBusy((async () => {
      if (!giId) {
        return;
      }
      const res: any = await rejectGi(giId, rejectNote.trim() || undefined);
      if (!res?.success) {
        notifications({
          title: "Error!",
          message: res?.message || "Could not ask for changes",
          type: "danger",
        });
        return;
      }
      applyView(unwrapGeoPayload(res));
      notifications({
        title: "Success!",
        message: res?.message || "Asked for changes",
        type: "success",
      });
    })());
  };

  const handleApproveFence = () => {
    void withBusy((async () => {
      if (!window.confirm("Approve this fence change? The live fence will update.")) {
        return;
      }
      const saved = await persist();
      if (!saved) {
        return;
      }
      const res: any = await approveGiChange(saved.id);
      if (!res?.success) {
        notifications({
          title: "Error!",
          message: res?.message || "Could not approve fence",
          type: "danger",
        });
        return;
      }
      applyView(unwrapGeoPayload(res));
      notifications({
        title: "Success!",
        message: res?.message || "Fence approved",
        type: "success",
      });
    })());
  };

  const handleKeepFence = () => {
    void withBusy((async () => {
      if (!giId) {
        return;
      }
      if (!window.confirm("Keep the current frozen fence and discard the proposed change?")) {
        return;
      }
      const res: any = await rejectGiChange(giId);
      if (!res?.success) {
        notifications({
          title: "Error!",
          message: res?.message || "Could not keep current fence",
          type: "danger",
        });
        return;
      }
      applyView(unwrapGeoPayload(res));
      notifications({
        title: "Success!",
        message: res?.message || "Current fence kept",
        type: "success",
      });
    })());
  };

  const handlePdf = async (file?: File) => {
    if (!file) {
      return;
    }
    setSaving(true);
    try {
      const url = await uploadGiPdf(file);
      setDocumentUrl(url);
      notifications({
        title: "Success!",
        message: "PDF uploaded",
        type: "success",
      });
    } catch (err: any) {
      notifications({
        title: "Error!",
        message: err?.message || "Could not upload PDF",
        type: "danger",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleGeojsonFile = async (file?: File) => {
    if (!file) {
      return;
    }
    if (file.size > 512 * 1024) {
      notifications({
        title: "Error!",
        message: "GeoJSON must be 512 KB or smaller",
        type: "danger",
      });
      return;
    }
    try {
      const text = await file.text();
      parseGeojsonObject(text);
      setGeojsonText(text);
      setGeojsonError("");
    } catch (err: any) {
      notifications({
        title: "Error!",
        message: err?.message || "Could not read that GeoJSON file",
        type: "danger",
      });
    }
  };

  const updatePlace = (index: number, field: keyof PlaceRow, value: string) => {
    setPlaces((rows) => rows.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  if (loading) {
    return <div className="gi-registry-page">Loading GI…</div>;
  }

  return (
    <div className="gi-registry-page">
      <div className="gi-registry-toolbar">
        <h2>{isNew ? "Register GI" : name || "GI details"}</h2>
        <div className="gi-form-actions" style={{ marginTop: 0 }}>
          <Button className="ResetBtn" onClick={() => history.push("/gi-registry")}>
            Back to list
          </Button>
          {readOnly && giId ? (
            <Button
              className="btn-hastkala-outline"
              onClick={() => history.push(`/gi-registry/${giId}/edit`)}
            >
              Edit
            </Button>
          ) : null}
          {!isSuperAdmin && giId ? (
            <Button
              className="btn-hastkala-outline"
              onClick={() => history.push(`/gi-registry/${giId}/authorised-user`)}
            >
              Authorised user
            </Button>
          ) : null}
        </div>
      </div>
      {!isNew && status ? (
        <p>
          <span className={giStatusClass(status, false)}>{giStatusLabel(status, false)}</span>
          {hasOpenChange ? (
            <span className={giStatusClass(status, true)} style={{ marginLeft: 8 }}>
              Edits pending review
            </span>
          ) : null}
        </p>
      ) : null}
      {frozen ? (
        <div className="gi-form-banner is-info">
          This GI is live and can still be edited. Studio changes wait for super admin review.
          Super admin can save live updates.
        </div>
      ) : null}
      {status === GI_STATUS.REJECTED && record?.rejectNote ? (
        <div className="gi-form-banner">Changes requested: {record.rejectNote}</div>
      ) : null}
      {hasOpenChange ? (
        <div className="gi-form-banner is-info">
          A fence change is waiting for review. Solid outline is the live fence; dashed is the proposal.
        </div>
      ) : null}

      <div className="gi-form-layout">
        <div className="gi-form-fields">
          <Label>GI name</Label>
          <Input
            value={name}
            readOnly={readOnly}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Banaras Brocades and Sarees"
          />
          <Label>Application number</Label>
          <Input
            value={applicationNumber}
            readOnly={readOnly}
            onChange={(e) => setApplicationNumber(e.target.value)}
            placeholder="e.g. 99"
          />
          {isNew ? (
            <p className="text-muted" style={{ fontSize: 13 }}>
              If this number is already live, saving adds that GI to your studio instead of creating a duplicate.
            </p>
          ) : null}
          <Label>Goods / craft</Label>
          <Input
            value={goodsDescription}
            readOnly={readOnly}
            onChange={(e) => setGoodsDescription(e.target.value)}
            placeholder="Handloom, silk, etc."
          />
          <Label>Goods classes</Label>
          <Input
            value={goodsClasses}
            readOnly={readOnly}
            onChange={(e) => setGoodsClasses(e.target.value)}
            placeholder="e.g. 23;24;25;26"
          />
          <Label>Category</Label>
          <Input
            value={category}
            readOnly={readOnly}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="TEXTILE_HANDLOOM_EMBROIDERY_OR_TEXTILE_CRAFT"
          />
          <Label>State</Label>
          <Input
            value={state}
            readOnly={readOnly}
            onChange={(e) => setState(e.target.value)}
            placeholder="Uttar Pradesh"
          />
          <Label>Geographical area</Label>
          <Input
            type="textarea"
            rows={3}
            value={geographicalAreaText}
            readOnly={readOnly}
            onChange={(e) => setGeographicalAreaText(e.target.value)}
            placeholder="Districts and centres from the GI journal"
          />
          <Label>Notes</Label>
          <Input
            type="textarea"
            rows={3}
            value={notes}
            readOnly={readOnly}
            onChange={(e) => setNotes(e.target.value)}
          />
          {boundaryStatus ? (
            <p>
              <span className="gi-boundary-chip">{boundaryStatus.replace(/_/g, " ")}</span>
              {seedApproximate ? (
                <span className="gi-status-chip is-change" style={{ marginLeft: 8 }}>
                  Approximate GeoJSON
                </span>
              ) : null}
            </p>
          ) : null}
          {centroidUseWarning ? (
            <div className="gi-form-banner">{centroidUseWarning}</div>
          ) : null}
          {ipIndiaDetailUrl ? (
            <p className="mb-2">
              <a href={ipIndiaDetailUrl} target="_blank" rel="noreferrer">
                IP India GI details
              </a>
              {sourceJournalPage ? ` · Journal page ${sourceJournalPage}` : ""}
              {lastVerifiedAt ? ` · Verified ${lastVerifiedAt}` : ""}
            </p>
          ) : null}
          <Label>IP India URL</Label>
          <Input
            value={ipIndiaDetailUrl}
            readOnly={readOnly}
            onChange={(e) => setIpIndiaDetailUrl(e.target.value)}
            placeholder="https://search.ipindia.gov.in/GIRPublicSearch/Application/Details/99"
          />
          <Label>Source document</Label>
          <Input
            value={sourceDocument}
            readOnly={readOnly}
            onChange={(e) => setSourceDocument(e.target.value)}
            placeholder="Geographical Indications Journal No.29"
          />
          <Label>Journal page</Label>
          <Input
            value={sourceJournalPage}
            readOnly={readOnly}
            onChange={(e) => setSourceJournalPage(e.target.value)}
            placeholder="e.g. 24 and 30-65"
          />
          {!readOnly ? (
            <>
          <Label>GI document (PDF, optional)</Label>
          <Input
            type="file"
            accept="application/pdf,.pdf"
            onChange={(e) => handlePdf(e.target.files?.[0])}
          />
          {documentUrl ? (
            <p className="mb-2">
              <a href={documentUrl} target="_blank" rel="noreferrer">
                Open uploaded PDF
              </a>
              {" · "}
              <button
                type="button"
                className="btn btn-link p-0"
                onClick={() => setDocumentUrl("")}
              >
                Remove
              </button>
            </p>
          ) : null}
            </>
          ) : documentUrl ? (
            <p className="mb-2">
              <a href={documentUrl} target="_blank" rel="noreferrer">
                Open uploaded PDF
              </a>
            </p>
          ) : null}

          <Label>Fence GeoJSON (editable)</Label>
          <p className="text-muted mb-2" style={{ fontSize: 13 }}>
            {seedApproximate
              ? "This polygon was generated from the journal centroid (about 25 km). It is not the legal GI map. Edit or replace it, then send the fence for approval."
              : "Paste or upload a Polygon (coordinates are longitude, latitude). This becomes the live fence. It is still not the legal GI map. If GeoJSON is present, it is used instead of the district hull."}
          </p>
          {!readOnly ? (
            <Input
              type="file"
              accept=".geojson,.json,application/geo+json,application/json"
              onChange={(e) => handleGeojsonFile(e.target.files?.[0])}
            />
          ) : null}
          <Input
            type="textarea"
            className="gi-geojson-input"
            rows={8}
            value={geojsonText}
            readOnly={readOnly}
            onChange={(e) => setGeojsonText(e.target.value)}
            placeholder='{"type":"Polygon","coordinates":[[[82.97,25.32],[83.27,25.26],[82.68,25.75],[82.97,25.32]]]}'
          />
          {geojsonText && !readOnly ? (
            <button
              type="button"
              className="btn btn-link p-0 mb-2"
              onClick={() => {
                setGeojsonText("");
                setGeojsonError("");
              }}
            >
              Clear GeoJSON
            </button>
          ) : null}
          {geojsonError ? <p className="text-danger" style={{ fontSize: 13 }}>{geojsonError}</p> : null}

          <Label>District / place points</Label>
          <p className="text-muted mb-2" style={{ fontSize: 13 }}>
            One latitude and longitude per district or place. Three or more points make a hull;
            fewer use an approximate radius. This is not the legal GI map.
          </p>
          {places.map((row, index) => (
            <div className="gi-place-row" key={index}>
              <Input
                placeholder="Place name"
                value={row.placeName}
                readOnly={readOnly}
                onChange={(e) => updatePlace(index, "placeName", e.target.value)}
              />
              <Input
                type="number"
                step="any"
                placeholder="Latitude"
                value={row.lat}
                readOnly={readOnly}
                onChange={(e) => updatePlace(index, "lat", e.target.value)}
              />
              <Input
                type="number"
                step="any"
                placeholder="Longitude"
                value={row.lng}
                readOnly={readOnly}
                onChange={(e) => updatePlace(index, "lng", e.target.value)}
              />
              {!readOnly ? (
                <Button
                  outline
                  size="sm"
                  disabled={places.length === 1}
                  onClick={() => setPlaces((rows) => rows.filter((_, i) => i !== index))}
                >
                  Remove
                </Button>
              ) : null}
            </div>
          ))}
          {!readOnly ? (
            <Button
              outline
              size="sm"
              className="mb-2"
              onClick={() => setPlaces((rows) => [...rows, emptyPlace()])}
            >
              Add district
            </Button>
          ) : null}
          {approximate ? (
            <p className="text-muted" style={{ fontSize: 13 }}>
              Fewer than three points: the fence is an approximate radius, not a hull.
            </p>
          ) : null}

          {isSuperAdmin && pendingReview && !isNew ? (
            <>
              <Label>Note to studio (optional)</Label>
              <Input
                type="textarea"
                rows={2}
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                placeholder="What should they change?"
              />
            </>
          ) : null}

          <div className="gi-form-actions">
            {isEdit && (isNew || pendingReview) ? (
              <Button className="ResetBtn" disabled={saving} onClick={handleSendForReview}>
                Send for review
              </Button>
            ) : null}
            {isEdit && frozen ? (
              <Button className="ResetBtn" disabled={saving} onClick={handleSendForReview}>
                {isSuperAdmin ? "Save live changes" : "Send changes for review"}
              </Button>
            ) : null}
            {isEdit && isSuperAdmin && pendingReview && !isNew ? (
              <>
                <Button className="ResetBtn" disabled={saving} onClick={handleApproveAndFreeze}>
                  Approve and freeze
                </Button>
                <Button
                  className="btn-hastkala-outline"
                  disabled={saving}
                  onClick={handleAskForChanges}
                >
                  Ask for changes
                </Button>
              </>
            ) : null}
            {isEdit && isSuperAdmin && frozen && hasOpenChange ? (
              <>
                <Button className="ResetBtn" disabled={saving} onClick={handleApproveFence}>
                  Approve fence
                </Button>
                <Button className="btn-hastkala-outline" disabled={saving} onClick={handleKeepFence}>
                  Keep current fence
                </Button>
              </>
            ) : null}
            <Button className="ResetBtn" onClick={() => history.push("/gi-registry")}>
              Back to list
            </Button>
          </div>
        </div>
        <GiFenceMap
          places={mapPlaces}
          liveHull={liveHull}
          proposedHull={proposedHull}
          looms={loomPins}
          hint={
            fenceSource === "GEOJSON"
              ? "Uploaded polygon fence. Still not the legal GI map."
              : "Approximate fence from district points, not the legal GI map."
          }
        />
      </div>
    </div>
  );
};

export default GiRegistryForm;
